import { api } from '@/services/api';
import { useTenants } from '@/services/tenants/useTenants';
import { useQueryClient } from '@tanstack/react-query';
import { REPORT_GROUPS_QUERY_KEY } from '@/hooks/api/reports/useInfiniteReportGroups';
import { REPORT_GROUP_ASSETS_QUERY_KEY } from '@/hooks/api/reports/useListReportGroupAssets';
import { Asset } from '@/api/generatedApi';
import { Alert } from '@mui/material';
import { BulkModal } from './BulkModal';
import { pluralize } from '@/utils/format';

interface BulkRemoveFromGroupModalProperties {
  open: boolean;
  onClose: () => void;
  selectedAssets: Asset[];
  reportId: string;
  groupId: string;
  assetCount: number;
  onRemoveComplete?: () => void;
}

export const BulkRemoveFromGroupModal = ({
  open,
  onClose,
  selectedAssets,
  reportId,
  groupId,
  assetCount,
  onRemoveComplete,
}: BulkRemoveFromGroupModalProperties) => {
  const { getTenantId } = useTenants();
  const queryClient = useQueryClient();

  const tenantId = getTenantId();
  const willRemoveAllAssets = selectedAssets.length === assetCount;

  const handleRemoveAsset = async (asset: Asset) => {
    await api.reports.removeAssetFromReportGroup(tenantId, reportId, groupId, asset.id);
  };

  const handleComplete = () => {
    queryClient.resetQueries({ queryKey: [REPORT_GROUPS_QUERY_KEY] });
    queryClient.resetQueries({ queryKey: [REPORT_GROUP_ASSETS_QUERY_KEY], exact: false });
    onRemoveComplete?.();
  };

  return (
    <BulkModal
      open={open}
      onClose={onClose}
      selectedItems={selectedAssets}
      onComplete={handleComplete}
      performOperation={handleRemoveAsset}
      labels={{
        title: 'Remove from Group',
        action: 'remove',
        actionPastTense: 'removed',
        actionPresent: 'Removing',
        confirmButton: 'Remove',
        itemSingular: 'asset',
        itemPlural: 'assets',
        description: count => {
          return `${pluralize(count, 'asset', 'assets')} will be removed from this group. This action cannot be undone.`;
        },
      }}
      buttonColor='error'
    >
      {willRemoveAllAssets && (
        <Alert severity='warning' sx={{ mt: 2 }}>
          You are removing all assets from this group. The group will be deleted.
        </Alert>
      )}
    </BulkModal>
  );
};
